/**
 * Contracts module API — typed calls over the shared axios instance
 * (`services/api`), which already attaches the Supabase JWT, X-Site-Id (from
 * the active site) and X-User-Id/X-User-Email on every request, and handles
 * 401 refresh + maintenance mode globally. Per the ixverify precedent
 * (`ixverify/CLAUDE.md`: "No new HTTP client, no ungated call"): this module
 * never talks to axios directly, and never re-implements that interceptor.
 *
 * The contracts guard (backend/src/middleware/contractsAccess.js, WP-02)
 * requires X-Site-Id on every route (plan §2.3, §4.9) — this file's job is
 * only to give the frontend typed, named functions over those routes, one
 * added per WP as the backend grows (Appendix A is the full surface; only
 * /health exists as of WP-02/WP-03).
 */
import api from '../../services/api';

export interface ContractsHealth {
  ok: boolean;
  feature: 'contracts';
  /**
   * Absent until the plan §12 backtest passes (WP-80/WP-81/WP-82). G12: "the
   * UI shows a 'backtest not passed' banner" until then — BacktestBanner
   * treats absent, false, and a failed request identically. Not yet emitted
   * by WP-02's /health handler; reserved here so the banner's contract is
   * already correct when a later WP starts returning it (from GET
   * /api/contracts/health or the dedicated GET /api/contracts/backtest/status
   * in Appendix A).
   */
  backtest_passed?: boolean;
}

/**
 * GET /api/contracts/health — behind the contracts guard (WP-02; X-Site-Id
 * required, super_admin bypasses the entitlement check). R4, R5.
 */
export async function getContractsHealth(): Promise<ContractsHealth> {
  const response = await api.get<ContractsHealth>('/api/contracts/health');
  return response.data;
}

// ---------------------------------------------------------------------------
// Studies and negotiations (plan §5.3, §7.5, §10.1, §10.3, Appendix A
// "Studies"; §13.4 WP-34). Every route sits behind the contracts guard,
// same as getContractsHealth above.
// ---------------------------------------------------------------------------

/** Display names for a study's four tags, resolved server-side (plan §7.5) — the backend, not this file, owns matching. */
export interface StudyTags {
  sponsorName: string | null;
  croName: string | null;
  therapeuticAreaName: string | null;
  indicationName: string | null;
}

/** plan §5.3. */
export interface Study {
  id: string;
  site_id: string;
  network_id: string;
  sponsor_id: string | null;
  cro_id: string | null;
  protocol_number: string;
  title: string | null;
  therapeutic_area_id: string | null;
  indication_id: string | null;
  recruitment_difficulty: number | null;
  enrollment_target: number | null;
  status: string;
  created_at: string;
  tags: StudyTags;
}

export type NegotiationKind = 'budget' | 'contract' | 'amendment_budget' | 'amendment_contract';
export type NegotiationStatus = 'open' | 'agreed' | 'executed' | 'closed';

/** plan §5.3, §10.1 (offered_version_id/final_version_id — set by the version flow, never posted directly), §10.3 (the four dates). */
export interface Negotiation {
  id: string;
  study_id: string;
  kind: NegotiationKind;
  label: string | null;
  status: NegotiationStatus;
  received_at: string | null;
  agreed_at: string | null;
  fe_at: string | null;
  email_received_at: string | null;
  offered_version_id: string | null;
  final_version_id: string | null;
  payment_days: number | null;
  payment_frequency: string | null;
  holdback_pct: number | null;
  created_at: string;
}

/** One master-list entry as a candidate carries it (services/contracts/masterLists, WP-33a). */
export interface MasterListCandidateEntry {
  id: string;
  list_type: string;
  official_name: string;
  section: string | null;
  code_type: string | null;
  code: string | null;
}

/** plan §7.5: a ranked candidate a person chooses from or types past — no score, however high, is ever auto-matched. */
export interface TagMatchCandidate {
  entry: MasterListCandidateEntry;
  score: number;
  matchedOn: string;
  matchLabel: 'official_name' | 'alias' | 'code';
  reasons: Array<{ code: string; detail: string; weight: number }>;
}

/** The 409 body createStudy/updateStudy return for an unresolved tag (plan §13.4 WP-34: "{ error, field, candidates }"). */
export interface TagUnresolvedErrorBody {
  error: string;
  field: 'sponsor' | 'cro' | 'therapeuticArea' | 'indication';
  candidates: TagMatchCandidate[];
}

/** A tag field's value: an entry id (already resolved, e.g. a chosen candidate) or free text the backend resolves through §7.5 matching. */
export type TagFieldValue = string;

export interface StudyInput {
  protocolNumber: string;
  status: string;
  title?: string;
  sponsor?: TagFieldValue;
  cro?: TagFieldValue;
  therapeuticArea?: TagFieldValue;
  indication?: TagFieldValue;
  recruitmentDifficulty?: number | null;
  enrollmentTarget?: number | null;
}

export interface StudyPatch extends Partial<StudyInput> {
  protocolNumber?: string;
  status?: string;
}

export interface StudyFilters {
  sponsorId?: string;
  croId?: string;
  protocolNumber?: string;
  status?: string;
  therapeuticAreaId?: string;
  indicationId?: string;
}

export interface NegotiationInput {
  kind: NegotiationKind;
  label?: string;
  status?: NegotiationStatus;
  receivedAt?: string | null;
  agreedAt?: string | null;
  feAt?: string | null;
  emailReceivedAt?: string | null;
  paymentDays?: number | null;
  paymentFrequency?: string | null;
  holdbackPct?: number | null;
}

/** PUT /negotiations/:id's patch (plan Appendix A: "dates, status, recruitment difficulty" — recruitment_difficulty itself lives on the study, §5.3, and is edited through updateStudy). */
export type NegotiationPatch = Partial<Omit<NegotiationInput, 'kind'>>;

/** GET /studies — always scoped to the caller's site (X-Site-Id) server-side. */
export async function listStudies(filters: StudyFilters = {}): Promise<Study[]> {
  const response = await api.get<{ studies: Study[] }>('/api/contracts/studies', { params: filters });
  return response.data.studies;
}

/** POST /studies — sponsor/cro/therapeuticArea/indication may be an entry id or text (§7.5); an unresolved text rejects with TagUnresolvedErrorBody (409). */
export async function createStudy(input: StudyInput): Promise<Study> {
  const response = await api.post<{ study: Study }>('/api/contracts/studies', input);
  return response.data.study;
}

/** GET /studies/:id */
export async function getStudy(id: string): Promise<Study> {
  const response = await api.get<{ study: Study }>(`/api/contracts/studies/${id}`);
  return response.data.study;
}

/** PUT /studies/:id — including recruitment_difficulty (1-5, R19). */
export async function updateStudy(id: string, patch: StudyPatch): Promise<Study> {
  const response = await api.put<{ study: Study }>(`/api/contracts/studies/${id}`, patch);
  return response.data.study;
}

/** GET /studies/:id/negotiations */
export async function listNegotiations(studyId: string): Promise<Negotiation[]> {
  const response = await api.get<{ negotiations: Negotiation[] }>(`/api/contracts/studies/${studyId}/negotiations`);
  return response.data.negotiations;
}

/** POST /studies/:id/negotiations */
export async function createNegotiation(studyId: string, input: NegotiationInput): Promise<Negotiation> {
  const response = await api.post<{ negotiation: Negotiation }>(`/api/contracts/studies/${studyId}/negotiations`, input);
  return response.data.negotiation;
}

/** PUT /negotiations/:id — the three turnaround dates plus the email date (§10.3), status, payment terms. offeredVersionId/finalVersionId are refused (set by the version flow, §10.1) — never send them. */
export async function updateNegotiation(id: string, patch: NegotiationPatch): Promise<Negotiation> {
  const response = await api.put<{ negotiation: Negotiation }>(`/api/contracts/negotiations/${id}`, patch);
  return response.data.negotiation;
}

// ---------------------------------------------------------------------------
// Master lists and matching (plan §5.2, §7.5, Appendix A "Master lists";
// §13.4 WP-33b). Network scoped (§4.9, decision D12) — the backend, not this
// file, resolves req.contracts.networkId; every function below is just a
// typed call over one of WP-33b's six routes.
// ---------------------------------------------------------------------------

/** §5.2's list_type enum — one controlled list per tag type. */
export type MasterListType =
  | 'therapeutic_area'
  | 'indication'
  | 'sponsor'
  | 'cro'
  | 'procedure'
  | 'fee'
  | 'payment_agent'
  | 'entity'
  | 'site_alias';

/** §5.2's section enum (procedure/fee entries only; null otherwise). */
export type MasterListSection =
  | 'per_patient'
  | 'unitized'
  | 'staff_rate'
  | 'stipend'
  | 'site_fee_required'
  | 'site_fee_other_required'
  | 'site_fee_other_desired'
  | 'overhead'
  | 'holdback'
  | 'screen_fail'
  | 'multi_year';

/** plan §5.2 cta_master_aliases. */
export interface MasterAlias {
  id: string;
  entry_id: string;
  alias: string;
  normalized: string;
  source: 'seed' | 'user' | 'parse';
  version_id: string | null;
  created_at: string;
}

/**
 * plan §5.2 cta_master_entries, the raw row — what POST /master/:type,
 * POST /master/match's exact result, POST /master/decide and the merge
 * route's loser/survivor all carry. NO `aliases` field: only GET
 * /master/:type joins aliases onto the row (MasterEntry below) — everywhere
 * else in Appendix A, an "entry" is this bare row.
 */
export interface MasterEntryRow extends MasterListCandidateEntry {
  network_id: string;
  merged_into: string | null;
  is_active: boolean;
  created_at: string;
}

/** plan §5.2 cta_master_entries, with its aliases (GET /master/:type's own shape only). */
export interface MasterEntry extends MasterEntryRow {
  aliases: MasterAlias[];
}

/** plan §5.2 cta_match_decisions. */
export interface MasterMatchDecision {
  id: string;
  list_type: MasterListType;
  raw_text: string;
  normalized: string;
  entry_id: string | null;
  outcome: 'matched' | 'new_entry' | 'rejected';
  decided_by: string;
  decided_at: string;
  version_id: string | null;
}

/** POST /master/match's exact-match shape (§7.5 steps 1-2). */
export interface MasterMatchExact {
  status: 'matched';
  matchLabel: 'official_name' | 'alias';
  entry: MasterEntryRow;
  matchedEntryId: string;
  matchedAlias: string | null;
  viaMerge: boolean;
  normalized: string;
}

/** POST /master/match's no-exact-match shape — a ranked list, never auto-matched (§7.5 step 3, G11). */
export interface MasterMatchUnmatched {
  status: 'unmatched';
  normalized: string;
  candidates: TagMatchCandidate[];
}

export type MasterMatchResult = MasterMatchExact | MasterMatchUnmatched;

/** POST /master/decide's result (§5.2: "when matched, becomes an alias"). */
export interface MasterDecideResult {
  decision: MasterMatchDecision;
  entry: MasterEntryRow | null;
  alias: MasterAlias | null;
  /** Set instead of `alias` when the matched text already normalizes to the entry's own official name. */
  aliasSkipped: string | null;
}

/** POST /master/:type/:id/merge-into/:survivor's result (§7.5's merge tool). */
export interface MasterMergeResult {
  loser: MasterEntryRow;
  survivor: MasterEntryRow;
  aliases_repointed: number;
  budget_lines_repointed: number;
  studies_repointed: Record<string, number>;
  /** cta_rc_datapoints still pointing at the loser — the repository has no re-pointer yet (WP-33a gap). */
  datapoints_pending: number;
  datapoints_gap: string | null;
}

export interface CreateMasterEntryInput {
  official_name: string;
  section?: MasterListSection | null;
  code_type?: string | null;
  code?: string | null;
}

/** GET /master/:type — live entries of that list_type for the caller's network, with their aliases. `q` searches name, alias and CPT code. */
export async function listMasterEntries(type: MasterListType, q?: string): Promise<MasterEntry[]> {
  const response = await api.get<{ entries: MasterEntry[] }>(`/api/contracts/master/${type}`, {
    params: q ? { q } : undefined,
  });
  return response.data.entries;
}

/** POST /master/:type — official_name required; section/code_type/code optional (§5.2). A duplicate active name is a 409. The returned row carries no `aliases` — a fresh entry has none. */
export async function createMasterEntry(type: MasterListType, input: CreateMasterEntryInput): Promise<MasterEntryRow> {
  const response = await api.post<{ entry: MasterEntryRow }>(`/api/contracts/master/${type}`, input);
  return response.data.entry;
}

/** POST /master/:type/:id/aliases — a user-typed spelling for an existing entry. */
export async function addMasterAlias(type: MasterListType, entryId: string, text: string): Promise<MasterAlias> {
  const response = await api.post<{ alias: MasterAlias }>(`/api/contracts/master/${type}/${entryId}/aliases`, { text });
  return response.data.alias;
}

/** POST /master/match { type, text } — never writes (G11). */
export async function matchMaster(type: MasterListType, text: string): Promise<MasterMatchResult> {
  const response = await api.post<MasterMatchResult>('/api/contracts/master/match', { type, text });
  return response.data;
}

/** POST /master/decide — outcome 'matched' needs entryId; 'new_entry' needs newEntry; 'rejected' needs neither. */
export async function decideMaster(
  type: MasterListType,
  text: string,
  outcome: 'matched' | 'new_entry' | 'rejected',
  options: { entryId?: string; newEntry?: CreateMasterEntryInput } = {}
): Promise<MasterDecideResult> {
  const response = await api.post<MasterDecideResult>('/api/contracts/master/decide', {
    type,
    text,
    outcome,
    ...(options.entryId ? { entryId: options.entryId } : {}),
    ...(options.newEntry ? { newEntry: options.newEntry } : {}),
  });
  return response.data;
}

/** POST /master/:type/:id/merge-into/:survivor — entitlement only (§4.2), no extra role check. */
export async function mergeMasterEntries(
  type: MasterListType,
  loserId: string,
  survivorId: string
): Promise<MasterMergeResult> {
  const response = await api.post<MasterMergeResult>(
    `/api/contracts/master/${type}/${loserId}/merge-into/${survivorId}`,
    {}
  );
  return response.data;
}

// ---------------------------------------------------------------------------
// Re-exports of other WPs' own api modules, so everything is importable from
// this one module (plan Appendix A; §13.4 WP-31, §13.5 WP-41/WP-43/WP-44,
// §13.8 WP-70/WP-71). intake.ts's own NegotiationKind is now an import of
// THIS file's type (MOUNT-2 wiring B) rather than a second declaration, so
// re-exporting it here is unambiguous: both names resolve to the same
// binding, not a duplicate.
// ---------------------------------------------------------------------------
export * from './rateCard';
export * from './notes';
export * from './medicare';
export * from './industry';
export * from './fees';
export * from './intake';
export * from './workspace';
export * from './metrics';
export * from './calculator';
export * from './versions';
export * from './clauses';
